import { useMemo } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { isDoctorUserGroup } from "~/types/auth";

/** A preferred name in parentheses, e.g. "Jason (Duo)" -> "Duo". */
const PREFERRED_NAME = /\(([^)]+)\)/;

const capitalizeFirstLetter = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** The name chat greetings address the current user by, or null if unknown.
 *
 *  Doctors are addressed formally, as "Dr. <lastName>" ("Doctor" without one),
 *  matching the Referral page greeting. Everyone else gets a first name,
 *  sourced from `User.firstName`, which ops curates and which is populated for
 *  every user, doctor and MA. A parenthetical is read as a preferred name, so
 *  "Jason (Duo)" greets as "Duo".
 *
 *  Falls back to the Cognito id-token only when the user has no record. That
 *  path is a poor greeting — the pool's app client can read just `email`
 *  (amplify/backend/auth/portalfc7df701/cli-inputs.json), so `given_name` and
 *  `name` never reach the token and it lands on the raw login id, greeting a
 *  user named "jdc" as "Jdc". Returns null until the record first resolves, so
 *  the greeting stays nameless rather than flashing the login id.
 */
export function useUserGreetingName(): string | null {
  const { authSession, authUser, userGroup } = useAuthSession();
  const { user, loading } = useCurrentUser();

  return useMemo(() => {
    // Only the first load is nameless. `refreshUser()` flips `loading` back to
    // true mid-session, and blanking an already-resolved name would make an
    // open greeting drop its name and pop back.
    if (loading && !user) {
      return null;
    }

    if (isDoctorUserGroup(userGroup)) {
      const lastName = user?.lastName?.trim();
      return lastName ? `Dr. ${lastName}` : "Doctor";
    }

    const firstName = user?.firstName?.trim();
    if (firstName) {
      const preferred = firstName.match(PREFERRED_NAME)?.[1]?.trim();
      return capitalizeFirstLetter(preferred || firstName);
    }

    const givenName = authSession?.tokens?.idToken?.payload?.["given_name"];
    if (typeof givenName === "string" && givenName.trim()) {
      return capitalizeFirstLetter(givenName.trim());
    }

    const fullName = authSession?.tokens?.idToken?.payload?.["name"];
    if (typeof fullName === "string" && fullName.trim()) {
      return capitalizeFirstLetter(fullName.trim().split(/\s+/)[0]);
    }

    const loginId = authUser?.signInDetails?.loginId;
    if (typeof loginId === "string" && loginId.trim()) {
      return capitalizeFirstLetter(loginId.trim().split("@")[0]);
    }

    return null;
  }, [user, loading, authSession, authUser, userGroup]);
}
